// Settings (side menu): what used to sit on Home but is about setting things up, not today's work.
// - Home: which cards show (nothing is deleted, only hidden), and the place for the weather (home port or this device).
// - Demo company: for showing VERA Studio to others (owners only).
// - Import from ServiceFusion (Marine owners, until it's done).
// - What VERA remembers, and Company safety (owners).

import { useEffect, useState } from "react";
import { Head, Icon, useStudio } from "../components/ui";
import { T } from "../lib/i18n";
import { HOME_CARDS, useHomePrefs } from "../lib/homePrefs";
import { findPlace } from "../lib/weather";
import { locationOn, setLocationOn } from "../lib/location";
import { seesPresence } from "../lib/presence";
import { crm, type CrmHome } from "../lib/crm";
import { vera } from "../api/vera";
import { MarineDemoCard } from "../components/MarineDemoCard";
import { PackDemoLoader } from "../components/BrainCard";
import { MemoryCard } from "../components/MemoryCard";
import { SafetyCard } from "../components/Safety";
import { CLIENT_PACKS } from "../config/clientPacks";
import { cleanError } from "../lib/m365";
import { listProposals, saveProposal, type Proposal } from "../lib/work";

export function Settings() {
  const { profile, getToken, user, openConsent } = useStudio();
  const [home, setHome] = useState<CrmHome | null>(null);
  const live = vera.mode === "live";
  useEffect(() => { if (live && profile.crm) crm.home(getToken).then(setHome).catch(() => setHome(null)); }, [live, profile.crm, getToken]);
  const owner = home?.me.role === "owner";
  const pack = CLIENT_PACKS.find((c) => c.key === profile.pack);
  const marine = profile.key === "client-marine";
  return (
    <>
      <Head title={T("Settings")} lead={T("How your Home looks, the weather's place, the demo company for showing VERA Studio, what VERA remembers and your company's safety.")} />
      <section className="card stack" style={{ gap: 10 }}>
        <h2>{T("Privacy, help and signing out")}</h2>
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          <button className="btn" onClick={openConsent}><Icon name="shield" size={15} /> {T("Privacy & consent")}</button>
          <button className="btn" onClick={() => window.dispatchEvent(new Event("vera-open-help"))}><Icon name="chat" size={15} /> {T("Help")}</button>
          <button className="btn" onClick={() => window.dispatchEvent(new Event("vera-sign-out"))}><Icon name="out" size={15} /> {T("Sign out")}</button>
        </div>
        <p className="faint small" style={{ margin: 0 }}>{T("What VERA may use (microphone, email and calendar, location) and how to reach us when something isn't right.")}</p>
      </section>
      <HomeSettings showOnline={seesPresence(user.roles)} />
      {live && profile.areas.includes("proposals") && <DeadlineSettings />}
      {owner && (marine || pack?.demo) && (
        <section className="card stack" style={{ gap: 8 }}>
          <h2>{T("Demo company")}</h2>
          <p className="faint" style={{ margin: 0, fontSize: 12.5 }}>{T("For showing VERA Studio to others: made-up customers and work, loaded as one import you can undo in Activity.")}</p>
          {marine ? <MarineDemoCard onDone={() => undefined} /> : <PackDemoLoader />}
        </section>)}
      {/* Service Fusion lives in Connections only (one place for every tool). */}
      {home && <MemoryCard />}
      {owner && <SafetyCard />}
    </>
  );
}

/** Proposal deadlines: add, change or remove the deadline of each open proposal (moved here from Home). */
function DeadlineSettings() {
  const { toast, go } = useStudio();
  const [list, setList] = useState<Proposal[] | null>(null);
  useEffect(() => { listProposals().then(setList).catch(() => setList([])); }, []);
  const open = (list ?? []).filter((p) => !["submitted", "awarded", "rejected"].includes(p.stage))
    .sort((a, b) => (a.deadline || "9999").localeCompare(b.deadline || "9999"));
  async function set(p: Proposal, deadline: string) {
    try { const s = await saveProposal({ ...p, deadline }); setList((l) => (l ?? []).map((x) => (x.id === s.id ? s : x))); toast(deadline ? T("Deadline saved.") : T("Deadline removed.")); }
    catch (e) { toast(cleanError(e)); }
  }
  return (
    <section className="card stack" style={{ gap: 8 }}>
      <div><h2>{T("Proposal deadlines")}</h2><p className="faint" style={{ margin: "4px 0 0", fontSize: 12.5 }}>{T("Add, change or remove the deadline of each open proposal. VERA reminds you as they come close.")}</p></div>
      {list === null ? <p className="faint">{T("Loading…")}</p> : !open.length ? <p className="faint">{T("No open proposals.")} <button className="btn sm" onClick={() => go("proposals")}>{T("Proposals")}</button></p> : (
        <div className="list">{open.map((p) => (
          <div className="li" key={p.id} style={{ gap: 8, flexWrap: "wrap" }}>
            <span className="grow" style={{ minWidth: 160 }}>{p.title}{p.client ? <span className="faint"> · {p.client}</span> : null}</span>
            <input type="date" value={p.deadline ?? ""} onChange={(e) => void set(p, e.target.value)} aria-label={`${T("Deadline")}: ${p.title}`} />
            {p.deadline && <button className="iconbtn ghost" onClick={() => void set(p, "")} aria-label={T("Remove the deadline")} title={T("Remove the deadline")}><Icon name="x" size={14} /></button>}
          </div>))}
        </div>)}
    </section>
  );
}

function HomeSettings({ showOnline }: { showOnline: boolean }) {
  const { toast } = useStudio();
  const { prefs, shows, toggle, setPlace } = useHomePrefs();
  const [q, setQ] = useState(prefs.place?.name ?? "");
  const [busy, setBusy] = useState(false);
  const [here, setHere] = useState(locationOn());
  async function find() {
    setBusy(true);
    try { const p = await findPlace(q.trim()); setPlace(p); setQ(p.name); toast(`${T("The weather is now for")} ${p.name}.`); }
    catch (e) { toast(cleanError(e)); } finally { setBusy(false); }
  }
  async function useDevice(on: boolean) {
    const ok = await setLocationOn(on);
    if (!ok) { toast(T("Your browser didn't allow the location. You can allow it in the browser's site settings.")); return; }
    setHere(on);
  }
  const cards = HOME_CARDS.filter((c) => c.id !== "online" || showOnline);
  return (
    <section className="card stack" style={{ gap: 12 }}>
      <div><h2>{T("Home")}</h2><p className="faint" style={{ margin: "4px 0 0", fontSize: 12.5 }}>{T("Choose what Home shows. A hidden card isn't deleted: switch it back on any time. Your choices follow you to every device.")}</p></div>
      <div className="grid g2" style={{ gap: 8 }}>
        {cards.map((c) => (
          <label key={c.id} className="row" style={{ gap: 10, alignItems: "flex-start", padding: "8px 10px", border: "1px solid var(--line)", borderRadius: 10, cursor: "pointer" }}>
            <input type="checkbox" checked={shows(c.id)} onChange={(e) => toggle(c.id, e.target.checked)} style={{ marginTop: 3 }} />
            <span><b style={{ fontSize: 13.5 }}>{T(c.label)}</b><span className="faint" style={{ display: "block", fontSize: 12 }}>{T(c.about)}</span></span>
          </label>))}
      </div>
      <div className="stack" style={{ gap: 6 }}>
        <b style={{ fontSize: 13.5 }}>{T("Weather for")}</b>
        <form className="row" style={{ gap: 8, flexWrap: "wrap" }} onSubmit={(e) => { e.preventDefault(); if (q.trim()) void find(); }}>
          <label className="search" style={{ flex: 1, minWidth: 220, maxWidth: 420 }}><Icon name="globe" size={15} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder={T("Your home port: a marina, a town or an address")} /></label>
          <button className="btn primary" disabled={busy || !q.trim()}>{busy ? T("Finding…") : T("Use this place")}</button>
          {prefs.place && <button type="button" className="btn" onClick={() => { setPlace(null); setQ(""); }}>{T("Clear")}</button>}
        </form>
        <label className="row" style={{ gap: 8, fontSize: 13 }}><input type="checkbox" checked={here} onChange={(e) => void useDevice(e.target.checked)} /> <span className="mute">{prefs.place ? T("Use where this device is when no home port is set") : T("Use where this device is (asks your browser)")}</span></label>
        <span className="faint" style={{ fontSize: 12 }}>{prefs.place ? `${T("Now:")} ${prefs.place.name}` : here ? T("Now: where this device is") : T("No place yet: Home shows the time without the weather.")}</span>
      </div>
    </section>
  );
}
